/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useEffect, useState } from 'react';
import { Box, Chip, Grow, Tooltip, useTheme } from '@mui/material';
import { useGetClassesOfRiskQuery } from '@/features/classesOfRisk/classesOfRiskApi';
import { useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { Skeleton } from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TransitionGroup } from 'react-transition-group';
import { RiskClassSelectContext } from '@/context/riskClassSelectContext';
import RiskClassTreeItem from './RiskClassTreeItem';
import SearchField from '../SearchField';
import RiskClassSelect from './RiskClassSelect';
import RiskClassAutocomplete from './RiskClassAutocomplete';

const RiskClassContext = ({ items, setItems, disabled, clientType, showTreeView = false }) => {
	const [expandedItems, setExpandedItems] = useState([]);
	const [searchValue, setSearchValue] = useState('');

	const theme = useTheme();
	const isDarkMode = theme.palette.mode === 'dark';

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const countryCode = userDetails?.broker?.country ?? 'NZ';
	const brokerGroupIds = userDetails?.brokerGroupArray;

	const { data: classesOfRisk, ...classesOfRiskQuery } = useGetClassesOfRiskQuery(
		{ countryCode, clientType, brokerGroupIds },
		{ skip: userDetails.isLoading || userDetails.isUninitialized },
	);
	const parents = (classesOfRisk ?? []).filter((i) => i.isParent);

	useEffect(() => {
		if (classesOfRiskQuery.isSuccess) {
			setExpandedItems([parents?.[0]?.id].filter(Boolean));
		}
	}, [classesOfRiskQuery.isSuccess]);

	const handleExpandedItemsChange = (event, itemIds) => {
		// Allow only one expanded tree item at a time
		setExpandedItems((prev) => {
			return itemIds.length < prev.length ? [] : [itemIds.find((i) => !prev.includes(i))];
		});
	};

	const handleItemChange = (newItems) => {
		console.log('New items', newItems);
		const finalNewItems = newItems.slice().filter((item) => {
			const conditions = item.conditions ?? {};
			const andConditions = conditions.and ?? [];
			const orConditions = conditions.or ?? [];

			const selectedIds = newItems.slice().map((i) => i.classOfRisk);

			const andValid =
				andConditions.length === 0 || andConditions.every((id) => selectedIds.includes(id));

			const orValid =
				orConditions.length === 0 || orConditions.some((id) => selectedIds.includes(id));

			return andValid && orValid;
		});

		setItems(finalNewItems);
	};

	const contextValue = {
		selectedItems: items,
		setSelectedItems: handleItemChange,
		expandedItems,
		setExpandedItems,
		searchValue,
		disabled,
	};

	return (
		<RiskClassSelectContext.Provider value={contextValue}>
			{parents.length === 1 && showTreeView ? (
				<RiskClassSelect {...{ disabled, clientType }} />
			) : (
				<Box>
					<TransitionGroup
						style={{
							flexWrap: 'wrap',
							width: '100%',
							alignItems: 'center',
							display: 'flex',
							rowGap: 2,
							columnGap: 2,
							...(items.length > 0 && { paddingBottom: '0.5em' }),
						}}
					>
						{items.map((item) => {
							const parent = parents.find((i) => i.classOfRisk === item.parent);
							return (
								<Grow
									key={item.id}
									style={{ transformOrigin: 'left center' }}
									appear={false}
									timeout={300}
								>
									<Tooltip
										title={`${parent?.label}: ${item.label}`}
										disableInteractive
									>
										<Chip
											label={item.label}
											size={'small'}
											sx={{
												backgroundColor: isDarkMode
													? 'transparent'
													: parent?.colour,
												border: isDarkMode
													? `1.5px solid ${parent?.colour}`
													: 'none',
												color: 'text.primary',
											}}
											onDelete={() =>
												setItems(items.filter((i) => i.id !== item.id))
											}
										/>
									</Tooltip>
								</Grow>
							);
						})}
					</TransitionGroup>
					{showTreeView ? (
						<>
							<Box>
								<SearchField
									value={searchValue}
									onChange={(event) => setSearchValue(event.target.value)}
									onReset={() => setSearchValue('')}
									helperText={''}
								/>
							</Box>
							<SimpleTreeView
								disableSelection
								expandedItems={expandedItems}
								onExpandedItemsChange={handleExpandedItemsChange}
								sx={{ pb: '1em' }}
							>
								{classesOfRiskQuery.isUninitialized ||
								classesOfRiskQuery.isLoading ? (
									<Skeleton />
								) : (
									parents.map((item) => {
										return (
											<RiskClassTreeItem
												key={item.id}
												{...{ item, disabled }}
											/>
										);
									})
								)}
							</SimpleTreeView>
						</>
					) : (
						<RiskClassAutocomplete {...{ disabled, clientType }} />
					)}
				</Box>
			)}
		</RiskClassSelectContext.Provider>
	);
};

export default RiskClassContext;
